import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import GuestbookExportModal from './GuestbookExportModal';
import { mockMemorials } from '../../data/mockData';

const {
  FiMessageSquare, FiTrendingUp, FiAlertCircle, FiCheckCircle,
  FiHeart, FiSun, FiWind, FiDownload, FiInbox,
} = FiIcons;

/* ── Aggregate all tributes across all mock memorials ─────────────── */
const allTributes = mockMemorials.flatMap(m =>
  (m.tributes || []).map(t => ({ ...t, memorialName: m.name }))
);

const GuestbookAnalytics = () => {
  const [exportOpen, setExportOpen] = useState(false);

  const stats = [
    { label: 'Total Tributes',  value: '1,420', sub: '+12% this month',   icon: FiMessageSquare, color: 'text-blue-500'    },
    { label: 'Approval Rate',   value: '98.2%', sub: 'High Engagement',   icon: FiCheckCircle,   color: 'text-green-500'  },
    { label: 'Flagged Content', value: '14',    sub: 'Requires Review',   icon: FiAlertCircle,   color: 'text-red-500'    },
    { label: 'Avg. Response',   value: '4.2h',  sub: 'To family replies', icon: FiTrendingUp,    color: 'text-accent-gold'},
  ];

  const giftDistribution = [
    { label: 'Candles',  count: 642, color: 'bg-amber-400',   icon: FiSun           },
    { label: 'Flowers',  count: 421, color: 'bg-rose-400',    icon: FiHeart         },
    { label: 'Doves',    count: 128, color: 'bg-blue-400',    icon: FiWind          },
    { label: 'Messages', count: 229, color: 'bg-accent-navy', icon: FiMessageSquare },
  ];

  return (
    <div className="space-y-8">

      {/* ── Header ───────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-serif text-accent-navy">Guestbook Analytics</h2>
          <p className="text-[10px] font-black uppercase tracking-widest text-text-muted mt-0.5">
            Engagement overview
          </p>
        </div>

        <motion.button
          onClick={() => setExportOpen(true)}
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.97 }}
          className="flex items-center gap-2 px-5 py-2.5 rounded-2xl text-xs font-black uppercase tracking-widest text-white shadow-md transition-all"
          style={{ background: 'linear-gradient(135deg, #1E3A5F 0%, #2a4f7c 100%)' }}
        >
          <SafeIcon icon={FiDownload} className="w-4 h-4" />
          Export Guestbook Data
        </motion.button>
      </div>

      {/* ── Stats ────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, idx) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            className="bg-white p-6 rounded-[32px] border border-brand-dark shadow-sm"
          >
            <div className={`w-10 h-10 rounded-xl bg-brand-light flex items-center justify-center mb-4 ${stat.color}`}>
              <SafeIcon icon={stat.icon} className="w-5 h-5" />
            </div>
            <h3 className="text-2xl font-serif text-accent-navy mb-1">{stat.value}</h3>
            <p className="text-[10px] font-black uppercase tracking-widest text-text-muted mb-2">{stat.label}</p>
            <p className="text-[9px] font-bold text-green-500">{stat.sub}</p>
          </motion.div>
        ))}
      </div>

      {/* ── Distribution ─────────────────────────────────────────────── */}
      <div className="bg-white p-8 rounded-[40px] border border-brand-dark shadow-sm">
        <h3 className="text-xl font-serif text-accent-navy mb-8">Interaction Distribution</h3>
        <div className="flex flex-col md:flex-row items-center gap-12">
          <div className="relative w-48 h-48 flex-shrink-0">
            <div className="absolute inset-0 rounded-full border-[16px] border-brand-light" />
            <div className="absolute inset-0 rounded-full border-[16px] border-accent-gold border-t-transparent border-l-transparent -rotate-12" />
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-serif text-accent-navy">Gifts</span>
              <span className="text-[10px] font-black text-text-muted uppercase">By Type</span>
            </div>
          </div>
          <div className="flex-grow grid grid-cols-2 gap-4 w-full">
            {giftDistribution.map(item => (
              <div
                key={item.label}
                className="p-4 bg-brand-light rounded-2xl border border-brand-dark flex items-center justify-between group hover:border-accent-gold transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg ${item.color} text-white flex items-center justify-center`}>
                    <SafeIcon icon={item.icon} className="w-4 h-4" />
                  </div>
                  <span className="text-xs font-bold text-accent-navy">{item.label}</span>
                </div>
                <span className="text-sm font-serif text-accent-gold">{item.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Tribute preview table ─────────────────────────────────────── */}
      <div className="bg-white p-8 rounded-[40px] border border-brand-dark shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-serif text-accent-navy">Recent Tributes</h3>
          <span className="text-[10px] font-black uppercase tracking-widest text-text-muted bg-brand-light px-3 py-1.5 rounded-full border border-brand-dark">
            {allTributes.length} total
          </span>
        </div>
        {allTributes.length === 0 ? (
          <div className="py-16 text-center">
            <div className="w-16 h-16 bg-brand-light rounded-2xl flex items-center justify-center mx-auto mb-4 border border-brand-dark">
              <SafeIcon icon={FiInbox} className="w-8 h-8 text-text-muted/30" />
            </div>
            <p className="text-sm font-serif text-accent-navy mb-1">No Tributes Recorded</p>
            <p className="text-xs text-text-muted font-light">
              Guestbook tributes will appear here once visitors start leaving messages.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-brand-dark">
                  <th className="text-left py-3 px-4 text-[9px] font-black uppercase tracking-widest text-text-muted">Author</th>
                  <th className="text-left py-3 px-4 text-[9px] font-black uppercase tracking-widest text-text-muted">Relationship</th>
                  <th className="text-left py-3 px-4 text-[9px] font-black uppercase tracking-widest text-text-muted">Message</th>
                  <th className="text-left py-3 px-4 text-[9px] font-black uppercase tracking-widest text-text-muted">Gift</th>
                  <th className="text-left py-3 px-4 text-[9px] font-black uppercase tracking-widest text-text-muted">Date</th>
                </tr>
              </thead>
              <tbody>
                {allTributes.map((t, i) => (
                  <tr key={i} className="border-b border-brand-light hover:bg-brand-light/40 transition-colors">
                    <td className="py-3 px-4 font-bold text-accent-navy">{t.author}</td>
                    <td className="py-3 px-4 text-text-muted text-[11px]">{t.relation || '—'}</td>
                    <td className="py-3 px-4 text-text-muted italic max-w-xs truncate">"{t.message}"</td>
                    <td className="py-3 px-4">
                      <span className="text-[10px] bg-brand-light border border-brand-dark px-2 py-1 rounded-full text-accent-navy font-bold">
                        {t.gift || '—'} {t.giftOption ? `(${t.giftOption})` : ''}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-[11px] text-text-muted whitespace-nowrap">{t.date || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Export Modal ─────────────────────────────────────────────── */}
      <GuestbookExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        stats={stats}
        gifts={giftDistribution}
        tributes={allTributes}
      />

    </div>
  );
};

export default GuestbookAnalytics;